import { User } from 'firebase/auth';
import { Recipe } from '../types/mealPlan';

const API_URL = process.env.REACT_APP_API_URL;

export type SeedRecipe = Recipe & {
  seedBatchId: string;
  seedingPrompt: string;
  mealPrepFriendly?: boolean;
};

const getHeaders = async (user: User) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${await user.getIdToken()}`,
});

export const fetchSeedQueue = async (
  user: User,
  limit = 20,
  startAfterId?: string
): Promise<SeedRecipe[]> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (startAfterId) params.set('startAfterId', startAfterId);

  const response = await fetch(
    `${API_URL}/api/admin/seed-queue?${params}`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) throw new Error('Failed to fetch seed queue');
  const data = await response.json();
  return data.recipes;
};

export const approveSeedRecipe = async (
  user: User,
  id: string
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/seed-queue/${id}/approve`,
    { method: 'POST', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to approve recipe');
  }
};

export const editSeedRecipe = async (
  user: User,
  id: string,
  updates: Partial<Recipe>
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/seed-queue/${id}/edit`,
    {
      method: 'POST',
      headers: await getHeaders(user),
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to edit recipe');
  }
};

export const generateSeedRecipeImage = async (
  user: User,
  id: string
): Promise<string> => {
  const response = await fetch(
    `${API_URL}/api/admin/seed-queue/${id}/generate-image`,
    { method: 'POST', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to generate image');
  }

  const data = await response.json();
  return data.imageUrl;
};

export const deleteSeedRecipe = async (
  user: User,
  id: string
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/seed-queue/${id}`,
    { method: 'DELETE', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to delete recipe');
  }
};

export const triggerCatalogSeeding = async (
  user: User,
  promptCount?: number,
  category?: 'breakfast' | 'lunch'
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/seed-catalog`,
    {
      method: 'POST',
      headers: await getHeaders(user),
      body: JSON.stringify({
        ...(promptCount && { promptCount }),
        ...(category && { category }),
      }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to trigger seeding');
  }
};

export const deleteAllSeedRecipes = async (user: User): Promise<number> => {
  const response = await fetch(
    `${API_URL}/api/admin/seed-queue`,
    { method: 'DELETE', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to delete all recipes');
  }

  const data = await response.json();
  return data.deleted;
};

export const updateCatalogRecipe = async (
  user: User,
  recipeId: string,
  updates: Partial<Recipe>
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/catalog-recipes/${recipeId}`,
    {
      method: 'PUT',
      headers: await getHeaders(user),
      body: JSON.stringify(updates),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to update catalog recipe');
  }
};

export const deleteCatalogRecipe = async (
  user: User,
  recipeId: string
): Promise<void> => {
  const response = await fetch(
    `${API_URL}/api/admin/catalog-recipes/${recipeId}`,
    { method: 'DELETE', headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to delete catalog recipe');
  }
};

export const fetchCatalogCuisines = async (user: User): Promise<string[]> => {
  const response = await fetch(
    `${API_URL}/api/admin/catalog-cuisines`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) throw new Error('Failed to fetch catalog cuisines');
  const data = await response.json();
  return data.cuisines;
};

export type TasteAxisType =
  | 'cuisine'
  | 'flavor'
  | 'technique'
  | 'ingredient'
  | 'format'
  | 'texture'
  | 'dietary';

export type TasteAxisSummary = {
  axisId: string;
  type: TasteAxisType;
  name: string;
  /** Whether an axis document (embedding) is currently stored. */
  exists: boolean;
  /** Catalog recipes carrying this attribute right now. */
  liveRecipeCount: number;
  /** Recipe count recorded at the last rebuild; null when no document exists. */
  storedRecipeCount: number | null;
  version: string | null;
  updatedAt: string | null;
  /** Rebuild floor for this axis type — axes below it are deleted on rebuild. */
  minRecipeCount: number;
};

export const fetchTasteAxes = async (
  user: User
): Promise<TasteAxisSummary[]> => {
  const response = await fetch(
    `${API_URL}/api/admin/taste-axes`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to fetch taste axes');
  }
  const data = await response.json();
  return data.axes;
};

export type AxisMemberRecipe = {
  id: string;
  name: string;
  cuisine: string | null;
  difficulty: string | null;
  imageUrl: string | null;
  usageCount: number;
  isCurated: boolean;
  createdAt: string | null;
  /**
   * Cosine similarity to the stored axis centroid (higher = closer). Null when
   * the recipe has no usable embedding or the axis has no stored centroid.
   */
  similarity: number | null;
};

export type AxisRecipesResult = {
  axis: {
    axisId: string;
    type: TasteAxisType;
    name: string;
    exists: boolean;
    memberCount: number;
    storedRecipeCount: number | null;
    minRecipeCount: number;
    updatedAt: string | null;
  };
  recipes: AxisMemberRecipe[];
};

export const fetchAxisRecipes = async (
  user: User,
  axisId: string
): Promise<AxisRecipesResult> => {
  const response = await fetch(
    `${API_URL}/api/admin/taste-axes/${encodeURIComponent(axisId)}/recipes`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to fetch axis recipes');
  }
  return response.json();
};

export const fetchCatalogRecipes = async (
  user: User,
  options: {
    limit?: number;
    offset?: number;
    cuisine?: string;
    difficulty?: string;
    servings?: number;
    search?: string;
    sort?: string;
    source?: string;
    category?: string;
    slot?: string;
    dietaryRestriction?: string;
  } = {}
): Promise<{ recipes: Recipe[]; total: number }> => {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit || 24));
  if (options.offset) params.set('offset', String(options.offset));
  if (options.cuisine) params.set('cuisine', options.cuisine);
  if (options.difficulty) params.set('difficulty', options.difficulty);
  if (options.servings) params.set('servings', String(options.servings));
  if (options.search) params.set('search', options.search);
  if (options.sort) params.set('sort', options.sort);
  if (options.source) params.set('source', options.source);
  if (options.category) params.set('category', options.category);
  if (options.slot) params.set('slot', options.slot);
  if (options.dietaryRestriction) params.set('dietaryRestriction', options.dietaryRestriction);

  const response = await fetch(
    `${API_URL}/api/admin/catalog-recipes?${params}`,
    { headers: await getHeaders(user) }
  );

  if (!response.ok) throw new Error('Failed to fetch catalog recipes');
  const data = await response.json();
  return { recipes: data.recipes, total: data.total };
};
